import { useState } from "react";
import { useEffectOnce } from "react-use";
import { Body, BodyDiv, Link, SectionHeader } from "../typography";
import { sanitizeHtml } from "~/utils/sanitize_html";
import { Button } from "../button";
import "./job_post.scss";
import { useTranslation } from "react-i18next";
import ArrowLeftIcon from "../icons/arrow_left";
import type { PayRange, HiringTeamMembers } from "~/types/jben/job_post";
import { useBoardConfiguration } from "~/hooks/use_board_configuration";
import { Tag, type TagProps } from "../tag";
import { useHiringTeamRenderer } from "~/hooks/use_hiring_team_renderer";
import { BadgePanel } from "./job_trust_profiles/badge_panel";
import { useTrackingParams } from "~/hooks/use_tracking_params";
import { JobAlert } from "~/components/job_alert";
import LocationIcon from "../icons/location";

const JOB_ALERT_VARIANTS = ["a", "b"] as const;
type JobAlertVariant = (typeof JOB_ALERT_VARIANTS)[number];

const randomJobAlertVariant = (): JobAlertVariant => {
  return JOB_ALERT_VARIANTS[Math.floor(Math.random() * JOB_ALERT_VARIANTS.length)];
};

interface Props {
  conclusion: string;
  content: string;
  introduction: string;
  location: string;
  onApplyClick?: (event: React.SyntheticEvent<HTMLButtonElement>) => void;
  title: string;
  internal?: boolean;
  payRanges?: PayRange[];
  tags?: TagProps[];
  hiringTeamMembers?: HiringTeamMembers;
  hiringPlanId?: number | string | null;
  urlToken?: string;
  companyName?: string;
  allowJobAlert?: boolean;
}

export const JobPost = ({
  conclusion,
  content,
  introduction,
  location,
  onApplyClick,
  title,
  internal,
  payRanges,
  tags,
  hiringTeamMembers = { hiring_managers: [], recruiters: [] },
  hiringPlanId,
  urlToken,
  companyName,
  allowJobAlert = false,
}: Props) => {
  const { t } = useTranslation("job_post");
  const {
    button_shape,
    cta_text,
    view_all_link,
    display_hiring_team,
    job_board_public_url,
    hide_app_description,
  } = useBoardConfiguration();
  const { buildRecruiterText, buildHiringManagerText } = useHiringTeamRenderer();
  const { withTrackingParams } = useTrackingParams();
  const [jobAlertVariant, setJobAlertVariant] = useState<JobAlertVariant>();
  const [jobAlertVariantResolved, setJobAlertVariantResolved] = useState(false);

  useEffectOnce(() => {
    if (window.ENV.JOB_ALERT_POST_VARIANTS) {
      setJobAlertVariant(randomJobAlertVariant());
    }

    setJobAlertVariantResolved(true);
  });

  const hiringManagerText = buildHiringManagerText(hiringTeamMembers.hiring_managers);
  const recruiterText = buildRecruiterText(hiringTeamMembers.recruiters);

  const handleApplyClick = (event: React.SyntheticEvent<HTMLButtonElement>) => {
    if (!onApplyClick) {
      return;
    }

    onApplyClick(event);
  };

  const renderPayRanges = () =>
    payRanges?.map((payRange) => (
      <div className="pay-range" key={payRange.title}>
        {payRange.description && (
          <div className="description">
            <BodyDiv>
              <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(payRange.description) }}></span>
            </BodyDiv>
          </div>
        )}
        <Body medium>{payRange.title}</Body>
        <Body>
          <bdi>{payRange.min}</bdi> - <bdi>{payRange.max}</bdi> <bdi>{payRange.currency_type}</bdi>
        </Body>
      </div>
    ));

  const showPayRanges = payRanges && payRanges.length > 0;

  const showHiringTeamMembers = !!(
    internal &&
    display_hiring_team &&
    (hiringManagerText || recruiterText)
  );

  const renderHiringTeamMembers = () => (
    <div className="job__contacts">
      <Body medium>{t("post.contacts")}</Body>
      {hiringManagerText && <Body>{hiringManagerText}</Body>}
      {recruiterText && <Body>{recruiterText}</Body>}
    </div>
  );

  return (
    <div>
      {!hide_app_description && (
        <>
          {view_all_link && (
            <Link href={withTrackingParams(job_board_public_url)}>
              <ArrowLeftIcon />
              {t("post.back")}
            </Link>
          )}
          <div className="job__tags">
            {tags?.map((tag, index) => (
              <Tag key={index} label={tag.label} />
            ))}
          </div>
          <div className="job__header">
            <div className="job__title">
              <SectionHeader as="h1" large>
                {title}
              </SectionHeader>
              {location && (
                <div className="job__location">
                  <LocationIcon size="sm" />
                  <div>{location}</div>
                </div>
              )}
            </div>
            {onApplyClick && (
              <Button
                ariaLabel={t("post.apply") || ""}
                onClick={handleApplyClick}
                shape={button_shape || undefined}
              >
                {cta_text || t("post.apply")}
              </Button>
            )}
          </div>
          <div className="job__description body">
            {showHiringTeamMembers && renderHiringTeamMembers()}
            {introduction && (
              <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(introduction) }}></div>
            )}
            {content && <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}></div>}
            {showPayRanges && <div className="job__pay-ranges">{renderPayRanges()}</div>}
            {conclusion && (
              <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(conclusion) }}></div>
            )}
          </div>
        </>
      )}
      <BadgePanel hiringPlanId={hiringPlanId} />
      {allowJobAlert && jobAlertVariantResolved && (
        <JobAlert
          urlToken={urlToken}
          companyName={companyName}
          onJobPost
          variant={jobAlertVariant}
        />
      )}
    </div>
  );
};
